import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import TrustedBy from "@/components/TrustedBy";
import Schedule from "@/components/Schedule";
import About from "@/components/About";
import BeDifferent from "@/components/BeDifferent";
import SocialMusicPlatforms from "@/components/SocialMusicPlatforms";
import HomePromoOffers from "@/components/HomePromoOffers";
import HomeCollections from "@/components/HomeCollections";
import HomeBestSellers from "@/components/HomeBestSellers";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import RequestRemix from "@/components/RequestRemix";
import { useAuth } from "@/lib/AuthContext";
import { useLocale } from "@/lib/LocaleContext";

/**
 * Sync the document title + primary meta description with the active
 * locale so /ar/ renders Arabic SEO strings instead of the static
 * English defaults from public/index.html. English routes keep the
 * static defaults verbatim (still identical to the values in
 * seoHead.home for EN).
 */
function useLocalisedSeoHead() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  useEffect(() => {
    const title = t("seoHead.home.title");
    const desc = t("seoHead.home.description");
    document.title = title;
    let el = document.querySelector('meta[name="description"]');
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", "description");
      document.head.appendChild(el);
    }
    el.setAttribute("content", desc);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      "href",
      `https://djspyofficial.com${lang === "ar" ? "/ar" : "/"}`,
    );
  }, [t, lang]);
}

export default function Home() {
  // Only render the banner wrapper when it will actually show content.
  // VerifyEmailBanner returns null for guests / admins / verified users,
  // and its wrapper `pt-24` used to leave a black spacer below the
  // transparent navbar in those cases — breaking the hero flush layout.
  const { user } = useAuth();
  useLocalisedSeoHead();
  const showBanner = Boolean(user && user.role !== "admin" && !user.email_verified);
  return (
    <>
      <Nav />
      <main data-testid="home-page" role="main">
        {showBanner && (
          <div className="pt-24 px-6 md:px-10 max-w-[1400px] mx-auto">
            <VerifyEmailBanner />
          </div>
        )}
        {/* Visually-hidden SEO copy for search engines and screen readers */}
        <p className="sr-only">
          DJ SPY Official — Dubai-based DJ, music producer and live performer.
          Discover exclusive Arabic remixes, EDM, Afro House, Afro Tech, Deep
          House, Moombahton, Reggaeton, Trap, Hip-Hop, Melodic Techno,
          Progressive House, Tech House, Big Room EDM, Arabic Club Edits,
          Festival Mashups, Club Intro Edits and Arabic/International Bootlegs.
          Purchase original productions and edits from the DJ SPY music store,
          or book DJ SPY for club nights, festivals, private events and
          corporate shows worldwide.
        </p>
        <Hero />
        <TrustedBy />
        <Schedule />
        <About />
        <BeDifferent />
        <HomePromoOffers />
        <HomeCollections />
        <HomeBestSellers />
        <RequestRemix />
        <Contact />
        <SocialMusicPlatforms />
      </main>
      <Footer />
    </>
  );
}
